/**
 * 敏感附件鉴权预览：登录态请求 /file/content → blob URL。
 * 头像/反馈等非 content 前缀仍走 CDN。
 *
 * App WebView：必须先等本次下发的最新 token，再请求（禁止用残留 localStorage）。
 */

import { waitForRNToken } from './rnBridge';
import {
  getOpenAccountAuthorization,
  isOpenAccountAppWebView,
  waitForOpenAccountBootstrap,
} from '../pages/mobile/account/open-progress/openAccountSession';
import { getActivityAuthorization } from '../activity/shared/activitySession';

const CONTENT_PREFIXES = ['upload/', 'base64/', 'open_account_sign/', 'w8ben/', 'go/'];

/**
 * 规范化相对路径。
 * @param path 原始路径
 * @returns 规范化路径
 */
function normalizeRel(path: string): string {
  return String(path || '')
    .trim()
    .replace(/^\/+/, '')
    .replace(/\\/g, '/');
}

/**
 * 是否 2go 外部收据路径。
 * @param path 路径
 * @returns true 为外部路径
 */
export function isCryptoReceiptPath(path: string): boolean {
  const p = normalizeRel(path);
  return p.startsWith('crypto/receipt/') || p.includes('/crypto/receipt/');
}

/**
 * 是否应走 /file/content。
 * @param path 路径
 * @returns true 鉴权下载
 */
export function isAuthContentPath(path: string): boolean {
  const p = normalizeRel(path);
  if (!p || /^https?:\/\//i.test(p) || isCryptoReceiptPath(p)) {
    return false;
  }
  return CONTENT_PREFIXES.some((prefix) => p.startsWith(prefix));
}

/**
 * CDN 明文直链（头像/反馈等）。
 * @param path 相对路径
 * @returns 完整 URL
 */
export function getCdnFileUrl(path: string): string {
  const base = String(import.meta.env.VITE_IMG_BASE_URL || '');
  const rel = normalizeRel(path);
  if (!rel) {
    return '';
  }
  if (/^https?:\/\//i.test(String(path).trim())) {
    return String(path).trim();
  }
  if (base.endsWith('/') && rel) {
    return base + rel;
  }
  return base + '/' + rel;
}

/**
 * 与 http-interceptor.resolveAuthorization 同源：开户 session → 活动 session → localStorage。
 * App 内先 await 最新 token，避免敏感图抢跑用旧 token。
 */
async function waitAndResolveAuthorization(): Promise<string> {
  if (isOpenAccountAppWebView()) {
    await waitForOpenAccountBootstrap();
  } else if (typeof window !== 'undefined' && Boolean((window as any).ReactNativeWebView)) {
    await waitForRNToken();
  }

  const openAccountAuthorization = getOpenAccountAuthorization();
  if (openAccountAuthorization !== undefined) {
    return openAccountAuthorization || '';
  }
  const activityAuthorization = getActivityAuthorization();
  if (activityAuthorization !== undefined) {
    return activityAuthorization || '';
  }
  return localStorage.getItem('token') || '';
}

/**
 * 拉取敏感附件明文 Blob；非敏感走 CDN fetch。
 * @param relativePath 相对路径或 http(s)
 * @returns 文件 Blob；路径为空时返回 null
 */
export async function getAuthFileBlob(relativePath: string): Promise<Blob | null> {
  if (!relativePath) {
    return null;
  }
  const raw = String(relativePath).trim();
  if (raw.startsWith('blob:')) {
    const res = await fetch(raw);
    if (!res.ok) {
      throw new Error(`blob fetch ${res.status}`);
    }
    return res.blob();
  }

  let fetchUrl = '';
  if (/^https?:\/\//i.test(raw)) {
    fetchUrl = raw;
  } else {
    const rel = normalizeRel(raw);
    if (!rel) {
      return null;
    }
    if (!isAuthContentPath(rel)) {
      fetchUrl = getCdnFileUrl(rel);
    } else {
      const apiBase = String(import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
      const token = await waitAndResolveAuthorization();
      if (!token) {
        throw new Error('未登录，无法加载敏感附件');
      }
      const res = await fetch(`${apiBase}/file/content?path=${encodeURIComponent(rel)}`, {
        credentials: 'include',
        headers: {
          Authorization: token,
          locale: navigator?.language || 'zh-CN',
        },
      });
      if (!res.ok) {
        throw new Error(`file content ${res.status}`);
      }
      return res.blob();
    }
  }

  if (!fetchUrl) {
    return null;
  }
  const res = await fetch(fetchUrl, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`file fetch ${res.status}`);
  }
  return res.blob();
}

/**
 * 拉取敏感附件明文 blob URL；非敏感返回 CDN。调用方须 revoke blob。
 * @param relativePath 相对路径或 http(s)
 * @returns 可赋给 img.src 的 URL
 */
export async function getAuthFileBlobUrl(relativePath: string): Promise<string> {
  if (!relativePath) {
    return '';
  }
  const raw = String(relativePath).trim();
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  if (raw.startsWith('blob:')) {
    return raw;
  }
  const rel = normalizeRel(raw);
  if (!rel) {
    return '';
  }
  if (!isAuthContentPath(rel)) {
    return getCdnFileUrl(rel);
  }
  const blob = await getAuthFileBlob(rel);
  if (!blob) {
    return '';
  }
  return URL.createObjectURL(blob);
}

/**
 * 释放 blob URL（非 blob 忽略）。
 * @param url 可能的 blob URL
 */
export function revokeAuthFileBlobUrl(url?: string | null): void {
  if (url && String(url).startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}
